/**
 * features/setup/ClassesSetup.tsx
 * Class CRUD. `form` is the level (1–5, AS, A2) which drives pass
 * marks and grade display in the grading engine. Class teacher /
 * assistant assignment feeds the permission helpers.
 *
 * Round 5: classTeacherId / assistantClassTeacherId updates now go
 * through lib/teacherAssignments helpers, which keep the Teacher
 * doc fields (classTeacherOf, isClassTeacher, assistantClassTeacherOf,
 * isAssistantClassTeacher) in sync with the Class doc fields. No
 * UI change vs the previous version — just two-sided writes.
 */
import { useState, useMemo } from 'react';
import {
  collection,
  deleteDoc,
  doc,
} from 'firebase/firestore';
// Batch B: undefined-safe write + save modal
import { addDoc, updateDoc } from '@/lib/firebase';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { Plus, Pencil, Trash2, GraduationCap } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { sanitizeText } from '@/lib/sanitize';
import { sortClasses } from '@/lib/classSort';
import {
  setClassTeacher,
  setAssistantClassTeacher,
} from '@/lib/teacherAssignments';
import { Button, Card, PageHeader, Spinner } from '@/components/ui/primitives';
import { FormModal } from '@/components/ui/glass';
import type { SchoolClass } from '@/types';

const FORMS = ['1', '2', '3', '4', '5', 'AS', 'A2'];

interface FormState {
  name: string;
  form: string;
  stream: string;
  classTeacherId: string;
  assistantClassTeacherId: string;
  archived: boolean;
}

const EMPTY: FormState = {
  name: '',
  form: '1',
  stream: '',
  classTeacherId: '',
  assistantClassTeacherId: '',
  archived: false,
};

export default function ClassesSetup() {
  const { classes, teachers, loading } = useData();
  const { userData } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<SchoolClass | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<SchoolClass | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });

  const sorted = useMemo(() => sortClasses(classes), [classes]);

  const teacherName = (id?: string | null) =>
    (id && teachers.find((t) => t.id === id)?.displayName) || '—';

  function openAdd() {
    setEditing(null);
    setForm(EMPTY);
    setShowModal(true);
  }
  function openEdit(c: SchoolClass) {
    setEditing(c);
    setForm({
      name: c.name,
      form: c.form,
      stream: c.stream ?? '',
      classTeacherId: c.classTeacherId ?? '',
      assistantClassTeacherId: c.assistantClassTeacherId ?? '',
      archived: c.archived ?? false,
    });
    setShowModal(true);
  }

  async function save() {
    const name = sanitizeText(form.name, 40);
    if (!name) {
      setMsg({ ok: false, text: 'Class name is required.' });
      return;
    }
    setBusy(true);
    setSaveModal({
      show: true,
      status: 'saving',
      message: editing ? 'Updating class…' : 'Adding class…',
    });
    try {
      const now = new Date().toISOString();

      if (editing) {
        // 1. Save the class's own fields first (name, form, stream)
        await updateDoc(doc(db, COLLECTIONS.classes, editing.id), {
          name,
          form: form.form,
          stream: sanitizeText(form.stream, 20),
          archived: form.archived,
          updatedAt: now,
          updatedBy: userData?.uid,
        });

        // 2. Two-sided sync for class-teacher / assistant changes
        if ((editing.classTeacherId ?? '') !== form.classTeacherId) {
          await setClassTeacher(
            editing.id,
            form.classTeacherId || null,
            { classes, teachers },
            userData?.uid,
          );
        }
        if ((editing.assistantClassTeacherId ?? '') !== form.assistantClassTeacherId) {
          await setAssistantClassTeacher(
            editing.id,
            form.assistantClassTeacherId || null,
            { classes, teachers },
            userData?.uid,
          );
        }

        setSaveModal({ show: true, status: 'success', message: 'Class updated.' });
      } else {
        // New class: create the class doc first, then run the sync
        // (helpers need a real classId).
        const newRef = await addDoc(collection(db, COLLECTIONS.classes), {
          name,
          form: form.form,
          stream: sanitizeText(form.stream, 20),
          archived: form.archived,
          classTeacherId: null,
          assistantClassTeacherId: null,
          createdAt: now,
          createdBy: userData?.uid,
          updatedAt: now,
          updatedBy: userData?.uid,
        });

        if (form.classTeacherId) {
          await setClassTeacher(
            newRef.id,
            form.classTeacherId,
            // Optimistic local view: the new class doesn't show up in
            // the live `classes` array yet, so add a stub.
            {
              classes: [
                ...classes,
                {
                  id: newRef.id,
                  name,
                  form: form.form,
                  classTeacherId: undefined,
                  assistantClassTeacherId: undefined,
                  createdAt: now,
                } as SchoolClass,
              ],
              teachers,
            },
            userData?.uid,
          );
        }
        if (form.assistantClassTeacherId) {
          await setAssistantClassTeacher(
            newRef.id,
            form.assistantClassTeacherId,
            {
              classes: [
                ...classes,
                {
                  id: newRef.id,
                  name,
                  form: form.form,
                  classTeacherId: form.classTeacherId || undefined,
                  assistantClassTeacherId: undefined,
                  createdAt: now,
                } as SchoolClass,
              ],
              teachers,
            },
            userData?.uid,
          );
        }
        setSaveModal({ show: true, status: 'success', message: 'Class added.' });
      }
      setShowModal(false);
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function remove(c: SchoolClass) {
    setBusy(true);
    try {
      // Before deleting the class, clear the back-references on
      // any teacher who currently points to it.
      if (c.classTeacherId) {
        await setClassTeacher(c.id, null, { classes, teachers }, userData?.uid);
      }
      if (c.assistantClassTeacherId) {
        await setAssistantClassTeacher(c.id, null, { classes, teachers }, userData?.uid);
      }
      await deleteDoc(doc(db, COLLECTIONS.classes, c.id));
      setMsg({ ok: true, text: `Removed ${c.name}.` });
      setConfirmDelete(null);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner label="Loading classes…" />;

  return (
    <div>
      <PageHeader
        title="Classes"
        subtitle={`${classes.length} class${classes.length === 1 ? '' : 'es'}`}
        actions={
          <Button icon={<Plus size={16} />} onClick={openAdd}>
            Add class
          </Button>
        }
      />

      {msg && (
        <div
          className={`mb-4 text-sm rounded-xl px-3 py-2.5 border ${
            msg.ok
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-red-50 text-red-700 border-red-200'
          }`}
        >
          {msg.text}
        </div>
      )}

      <Card className="overflow-hidden">
        {sorted.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-12">
            No classes yet. Add Form 1A, Form 2B, etc.
          </p>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {sorted.map((c) => (
              <div
                key={c.id}
                className="flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-sm shrink-0">
                  {c.name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-800 dark:text-white flex items-center gap-2">
                    {c.name}
                    {c.archived && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Archived</span>
                    )}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500 mt-0.5">
                    <span>Form {c.form}</span>
                    {c.stream && <span>· Stream {c.stream}</span>}
                    <span>
                      · Class teacher:{' '}
                      <strong className="text-gray-700 dark:text-gray-300">
                        {teacherName(c.classTeacherId)}
                      </strong>
                    </span>
                    <span>
                      · Assistant:{' '}
                      <strong className="text-gray-700 dark:text-gray-300">
                        {teacherName(c.assistantClassTeacherId)}
                      </strong>
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => openEdit(c)}
                    className="p-2 rounded-lg hover:bg-primary/10 text-primary"
                    title="Edit class"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => setConfirmDelete(c)}
                    className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500"
                    title="Delete class"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Edit modal */}
      <FormModal
        open={showModal}
        onClose={() => setShowModal(false)}
        icon={GraduationCap}
        title={editing ? 'Edit class' : 'New class'}
        maxWidth="max-w-md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowModal(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={save} loading={busy}>
              {editing ? 'Save changes' : 'Add class'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Form 3 A"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Form level
                  </label>
                  <select
                    value={form.form}
                    onChange={(e) => setForm({ ...form, form: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm"
                  >
                    {FORMS.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Stream
                  </label>
                  <input
                    type="text"
                    value={form.stream}
                    onChange={(e) => setForm({ ...form, stream: e.target.value })}
                    placeholder="A"
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                  <GraduationCap size={12} /> Class teacher
                </label>
                <select
                  value={form.classTeacherId}
                  onChange={(e) => setForm({ ...form, classTeacherId: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm"
                >
                  <option value="">— None —</option>
                  {[...teachers].sort((a, b) => a.lastName.localeCompare(b.lastName)).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.displayName}
                      {t.classTeacherOf && t.classTeacherOf !== editing?.id
                        ? ` (currently of ${teacherName(t.id)} — will be reassigned)`
                        : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                  <GraduationCap size={12} /> Assistant class teacher
                </label>
                <select
                  value={form.assistantClassTeacherId}
                  onChange={(e) => setForm({ ...form, assistantClassTeacherId: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm"
                >
                  <option value="">— None —</option>
                  {[...teachers].sort((a, b) => a.lastName.localeCompare(b.lastName)).map((t) => (
                    <option key={t.id} value={t.id}>{t.displayName}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-start gap-2 rounded-lg border border-gray-200 dark:border-gray-700 p-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.archived}
                  onChange={(e) => setForm({ ...form, archived: e.target.checked })}
                  className="mt-0.5 rounded"
                />
                <span className="text-xs text-gray-700 dark:text-gray-300">
                  <span className="font-medium">Archived class (alumni / records only)</span>
                  <br />
                  Students here are kept for record-keeping but <strong>excluded from active rosters</strong> —
                  marks compliance, attendance, analytics. Use this for your Alumni / left-students class.
                </span>
              </label>
        </div>
      </FormModal>


      {/* Delete confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <Card glass className="w-full max-w-sm p-6 animate-fade-in">
            <h2 className="text-lg font-bold">Remove class?</h2>
            <p className="text-sm text-gray-500 mt-2">
              Remove <strong>{confirmDelete.name}</strong>? Any teacher assignments to this class will be cleared. Past marks remain in records.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={() => setConfirmDelete(null)} disabled={busy}>
                Cancel
              </Button>
              <Button onClick={() => remove(confirmDelete)} loading={busy}>
                Remove
              </Button>
            </div>
          </Card>
        </div>
      )}
      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
    </div>
  );
}
